import { debounce } from 'lodash'
import { Difficulty, State } from '../hooks/gameReducer'
import { Group } from '../hooks/wordReducer'
import {
  createId,
  getWordsFromLocalStorage,
  getItem,
  idKey,
} from './localStorage'
import { Word } from './statuses'
import { getCurrentUserId } from './googleAuth'

const API_URL = process.env.REACT_APP_API_V2_URL || '/api-v2.php'

/**
 * Állapot mentése az API-v2-n keresztül (ami Firebase-be menti)
 */
export const sendStateToAPI = (state: State) => {
  // Write the state under its own id. A Google login must not
  // overwrite the linked profile with an anonymous session's state.
  const sessionId = state.id || getCurrentUserId() || createId()

  const url = API_URL.startsWith('http')
    ? API_URL
    : `${window.location.protocol}//${window.location.host}${API_URL}`

  const data = new FormData()
  data.append('id', sessionId)
  data.append('state', JSON.stringify(state))

  return fetch(url, {
    method: 'POST',
    body: data,
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      return response.json()
    })
    .catch((error) => {
      console.error('Error sending state to API:', error)
      // Visszaadjuk az üres globális statisztikát hiba esetén
      return {}
    })
}

/**
 * Állapot lekérése az API-v2-ből (ami Firebase-ből tölti be)
 */
export const getStateFromAPI = (): Promise<Partial<State> | null> => {
  const url = API_URL.startsWith('http')
    ? API_URL
    : `${window.location.protocol}//${window.location.host}${API_URL}`

  // Priority: 1) localStorage 'szozat-gameState', 2) Google userId, 3) old idKey, 4) create new
  const localStorageUserId = localStorage.getItem('szozat-gameState')
  const googleUserId = getCurrentUserId()
  const storedId = getItem(idKey)
  const userId =
    localStorageUserId ||
    googleUserId ||
    (storedId ? JSON.parse(storedId).id : createId())

  console.log('getStateFromAPI - userId sources:', {
    localStorageUserId,
    googleUserId,
    storedId: storedId ? JSON.parse(storedId).id : null,
    finalUserId: userId,
  })

  const params = new URLSearchParams()
  params.append('state', 'true')
  params.append('id', userId)

  return fetch(`${url}?${params.toString()}`, {
    method: 'GET',
  })
    .then((response) => {
      console.log('getStateFromAPI - response status:', response.status)
      if (!response.ok) {
        if (response.status === 404) {
          // Nincs még állapot mentve
          console.log('getStateFromAPI - No state found (404)')
          return null
        }
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      return response.json()
    })
    .then((data) => {
      console.log(
        'getStateFromAPI - received data:',
        data ? 'yes' : 'no',
        data?.id
      )
      return data
    })
    .catch((error) => {
      console.error('Error getting state from API:', error)
      return null
    })
}

/**
 * Globális statisztikák lekérése az API-v2-ből
 */
export const getGlobalStatsFromAPI = () => {
  const url = API_URL.startsWith('http')
    ? API_URL
    : `${window.location.protocol}//${window.location.host}${API_URL}`
  const params = new URLSearchParams()
  params.append('global', 'true')
  params.append('id', createId())

  return fetch(`${url}?${params.toString()}`, {
    method: 'GET',
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      return response.json()
    })
    .catch((error) => {
      console.error('Error getting global stats from API:', error)
      return {}
    })
}

/**
 * Debounced state mentés
 */
export const debouncingStateToAPI = debounce(
  (...params: Parameters<typeof sendStateToAPI>) => sendStateToAPI(...params),
  100,
  { trailing: true }
)

/**
 * Statikus szavak lekérése (változatlan a régi API-ból)
 */
export const getStaticWords = (
  difficulty: Difficulty,
  resolver: (value: {
    group: Group
    difficulty: Difficulty
    words: Word[]
  }) => void
) => {
  const urls: Record<Group, string> = {
    all: `/words/hungarian-word-letter-list-${difficulty}.json`,
    selected: `/words/hungarian-puzzles-${difficulty}.json`,
    random: `/words/hungarian-puzzles-all-${difficulty}.json`,
  }

  let timeout = 0
  Object.entries(urls).forEach(([group, url]) => {
    if (!getWordsFromLocalStorage(group as Group, difficulty)?.length) {
      timeout = timeout++
      setTimeout(() => {
        fetch(url, {
          method: 'GET',
        })
          .then((response) => response.json())
          .then((words) =>
            resolver({
              group: group as Group,
              difficulty,
              words: words as Word[],
            })
          )
      }, timeout * 100)
    }
  })
}
